import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { useResourcePacket } from '../../context/ResourcePacketContext';
import { toast } from 'sonner';
import { FileText, Trash2, Download, Mail, ArrowLeft, Phone, Globe, MapPin, ShoppingBasket } from 'lucide-react';

const ResourcePacket = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { items, removeItem, clear, count } = useResourcePacket();
  const [form, setForm] = useState({ county: '', client_name: '', notes: '', provider_name: '', provider_agency: '', provider_phone: '', recipient_email: '', cc_self: true });
  const [busy, setBusy] = useState('');

  useEffect(() => { document.title = 'My Resource Packet | Community Resource Directory'; }, []);
  useEffect(() => { if (!authLoading && !isAuthenticated) navigate('/login'); }, [authLoading, isAuthenticated, navigate]);
  useEffect(() => { if (user?.name && !form.provider_name) setForm((f) => ({ ...f, provider_name: user.name })); }, [user]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payload = () => ({
    resource_ids: items.map((r) => r.resource_id),
    county: form.county, client_name: form.client_name, notes: form.notes,
    provider_name: form.provider_name, provider_agency: form.provider_agency, provider_phone: form.provider_phone,
  });

  const download = async (openForPrint = false) => {
    if (count === 0) { toast.error('Your packet is empty.'); return; }
    setBusy(openForPrint ? 'print' : 'download');
    try {
      const res = await axios.post(`${API_URL}/resources/packet/pdf`, payload(), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (openForPrint) {
        const w = window.open(url, '_blank');
        if (!w) toast.error('Please allow pop-ups to print.');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = 'resource-packet.pdf'; document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error('Could not generate the PDF. Please try again.'); } finally { setBusy(''); }
  };

  const sendEmail = async () => {
    if (count === 0) { toast.error('Your packet is empty.'); return; }
    if (!form.recipient_email || !form.recipient_email.includes('@')) { toast.error('Enter a valid recipient email.'); return; }
    setBusy('email');
    try {
      const res = await axios.post(`${API_URL}/resources/packet/email`, { ...payload(), recipient_email: form.recipient_email, cc_self: form.cc_self });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send the email.'); } finally { setBusy(''); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <button onClick={() => navigate('/community/resources')} className="text-secondary text-sm hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to Resources</button>
          <div className="flex items-center gap-2 mt-3">
            <FileText className="w-7 h-7 text-secondary" />
            <h1 className="font-serif text-3xl font-bold">My Resource Packet</h1>
          </div>
          <p className="text-white/80 mt-2">Build a custom resource list for your client, then print it or email it — always free.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-6">
        {/* Items */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl font-bold text-primary">{count} Resource{count === 1 ? '' : 's'}</h2>
            {count > 0 && <button onClick={() => { clear(); toast('Packet cleared'); }} className="text-sm text-gray-500 hover:text-red-600" data-testid="clear-packet">Clear all</button>}
          </div>
          {count === 0 ? (
            <div className="bg-white rounded-2xl border p-10 text-center" data-testid="packet-empty">
              <ShoppingBasket className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Your packet is empty.</p>
              <p className="text-gray-400 text-sm mt-1">Browse the directory and tap "Add to Packet" on any resource.</p>
              <Button onClick={() => navigate('/community/resources')} className="mt-4 bg-secondary hover:bg-secondary-hover text-white" data-testid="browse-resources-btn">Browse Resources</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((r) => (
                <div key={r.resource_id} className="bg-white rounded-2xl border p-4 flex items-start justify-between gap-3" data-testid={`packet-item-${r.resource_id}`}>
                  <div>
                    <span className="text-xs font-semibold text-secondary uppercase">{r.category}</span>
                    <h3 className="font-serif text-base font-bold text-primary leading-tight">{r.name}</h3>
                    {r.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{r.description}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                      {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                      {r.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> Website</span>}
                      {(r.city || r.state) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {[r.city, r.state].filter(Boolean).join(', ')}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => removeItem(r.resource_id)} className="text-red-600 border-red-200 flex-shrink-0" data-testid={`remove-packet-${r.resource_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Packet details + actions */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border p-5 lg:sticky lg:top-24 space-y-4">
            <h2 className="font-serif text-lg font-bold text-primary">Packet Details</h2>
            <div>
              <Label>County</Label>
              <Input value={form.county} onChange={(e) => set('county', e.target.value)} placeholder="e.g. Travis County" data-testid="packet-county-input" />
            </div>
            <div>
              <Label>Client name (optional)</Label>
              <Input value={form.client_name} onChange={(e) => set('client_name', e.target.value)} placeholder="Prepared for" data-testid="packet-client-input" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Any instructions or context" data-testid="packet-notes-input" />
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer text-primary font-medium">Add my info to the printout</summary>
              <div className="space-y-2 mt-2">
                <Input value={form.provider_name} onChange={(e) => set('provider_name', e.target.value)} placeholder="Your name" data-testid="packet-provider-name" />
                <Input value={form.provider_agency} onChange={(e) => set('provider_agency', e.target.value)} placeholder="Agency (optional)" data-testid="packet-provider-agency" />
                <Input value={form.provider_phone} onChange={(e) => set('provider_phone', e.target.value)} placeholder="Phone (optional)" data-testid="packet-provider-phone" />
              </div>
            </details>

            <div className="pt-2 border-t space-y-2">
              <Button onClick={() => download(true)} disabled={busy || count === 0} className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="print-packet-btn"><FileText className="w-4 h-4 mr-2" /> {busy === 'print' ? 'Preparing…' : 'Print PDF'}</Button>
              <Button onClick={() => download(false)} disabled={busy || count === 0} variant="outline" className="w-full" data-testid="download-packet-btn"><Download className="w-4 h-4 mr-2" /> {busy === 'download' ? 'Preparing…' : 'Download PDF'}</Button>
            </div>

            <div className="pt-2 border-t space-y-2">
              <Label>Email the packet to</Label>
              <Input type="email" value={form.recipient_email} onChange={(e) => set('recipient_email', e.target.value)} placeholder="recipient@email.com" data-testid="packet-email-input" />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <Checkbox checked={form.cc_self} onCheckedChange={(v) => set('cc_self', !!v)} data-testid="packet-cc-self" /> Send a copy to me too
              </label>
              <Button onClick={sendEmail} disabled={busy || count === 0} className="w-full bg-secondary hover:bg-secondary-hover text-white" data-testid="email-packet-btn"><Mail className="w-4 h-4 mr-2" /> {busy === 'email' ? 'Sending…' : 'Email Packet'}</Button>
            </div>
            <p className="text-xs text-gray-400">Always free. Information is subject to change; listing is not an endorsement.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePacket;
